/**
 * A radix-2 FFT, because Phase 5 needs one and the platform does not offer it.
 *
 * `AnalyserNode` will FFT the audio for you, which is the wrong signal: FFT-ing
 * raw audio finds pitch, and a trill's identity is how fast its *envelope*
 * pulses. There is no browser API that transforms an arbitrary array, so here it
 * is — a hundred lines, no dependency, and testable against signals whose answer
 * is known in advance.
 *
 * Sizes must be powers of two. Phase 5 uses 256 points at ~375 Hz, which is a
 * 683 ms window with 1.46 Hz bins.
 */

/**
 * In-place complex FFT. `re` and `im` are modified.
 *
 * @param {Float64Array} re
 * @param {Float64Array} im
 */
export function fft(re, im) {
  const n = re.length;
  if (n !== im.length) throw new RangeError('re and im must be the same length');
  if (n < 2 || (n & (n - 1)) !== 0) throw new RangeError(`length must be a power of two, got ${n}`);

  // Bit-reversal permutation, iteratively: for each i, j is i with its bits
  // reversed, and the two are swapped once.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const step = (-2 * Math.PI) / len;
    const half = len >> 1;
    for (let i = 0; i < n; i += len) {
      for (let k = 0; k < half; k++) {
        const angle = step * k;
        const wr = Math.cos(angle);
        const wi = Math.sin(angle);
        const a = i + k;
        const b = a + half;
        const vr = re[b] * wr - im[b] * wi;
        const vi = re[b] * wi + im[b] * wr;
        re[b] = re[a] - vr;
        im[b] = im[a] - vi;
        re[a] += vr;
        im[a] += vi;
      }
    }
  }
}

/**
 * Hann window of length n, cached — it is the same array every hop.
 *
 * Hann rather than rectangular because the window is 683 ms of a signal that
 * does not repeat over it. Without a taper, the discontinuity at the edges
 * spreads energy across every bin, and a trill's peak has to compete with the
 * leakage from the much larger DC and low-frequency content of the envelope.
 *
 * @param {number} n
 * @returns {Float64Array}
 */
const windows = new Map();
export function hann(n) {
  let w = windows.get(n);
  if (!w) {
    w = new Float64Array(n);
    for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
    windows.set(n, w);
  }
  return w;
}

/**
 * Magnitude spectrum of a real signal, windowed and mean-removed.
 *
 * The mean is removed because the envelope of any audible sound is a large
 * positive number modulated by a small one. Leave the mean in and bin 0 holds
 * the loudness of the sound, its skirts reach into the band of interest, and a
 * loud steady vowel looks more like a trill than a quiet real trill does.
 *
 * @param {ArrayLike<number>} signal  length must be a power of two
 * @param {object} [scratch] reusable {re, im, mag} to avoid per-hop allocation
 * @returns {Float64Array} magnitudes for bins 0..n/2, scaled to the input's units
 */
export function magnitudeSpectrum(signal, scratch = null) {
  const n = signal.length;
  const re = scratch?.re?.length === n ? scratch.re : new Float64Array(n);
  const im = scratch?.im?.length === n ? scratch.im : new Float64Array(n);
  const mag = scratch?.mag?.length === n / 2 + 1 ? scratch.mag : new Float64Array(n / 2 + 1);

  let mean = 0;
  for (let i = 0; i < n; i++) mean += signal[i];
  mean /= n;

  const w = hann(n);
  for (let i = 0; i < n; i++) {
    re[i] = (signal[i] - mean) * w[i];
    im[i] = 0;
  }

  fft(re, im);

  // Hann's coherent gain is 0.5, and a real signal's energy is split between
  // the positive and negative halves — hence n/4 rather than n/2. A sinusoid of
  // amplitude A therefore reads back as A.
  const scale = n / 4;
  for (let k = 0; k <= n / 2; k++) mag[k] = Math.hypot(re[k], im[k]) / scale;
  return mag;
}

/**
 * Sub-bin peak position by parabolic interpolation through three magnitudes.
 *
 * At 1.46 Hz per bin a whole-bin answer quantises modRate into steps you can
 * hear the difference between, and the stability test Phase 5 detects on would
 * be measuring the quantiser rather than the trill.
 *
 * @param {ArrayLike<number>} mag
 * @param {number} k  index of the peak bin
 * @returns {number} interpolated bin index
 */
export function refinePeak(mag, k) {
  if (k <= 0 || k >= mag.length - 1) return k;
  const a = mag[k - 1];
  const b = mag[k];
  const c = mag[k + 1];
  const denom = a - 2 * b + c;
  if (denom === 0) return k;
  const shift = (0.5 * (a - c)) / denom;
  return Math.abs(shift) <= 1 ? k + shift : k;
}
